import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { IStoreRedux } from 'Types/Reducers'

import Typography from '@material-ui/core/Typography'
import Autocomplete from '@material-ui/lab/Autocomplete'

import { CampoDeTextoOutlined } from '../styles'
import { get } from '../../../../_axios/requisicao'

interface IEscolaridade {
  id: number
  nome: string
}

interface IFormacao {
  id: number
  nome: string
}

function CampoEscolaridade() {
  const escolaridade = useSelector<IStoreRedux, string | null>(state => state.dados.escolaridade)
  const idEscolaridade = useSelector<IStoreRedux, number | null>(state => state.dados.grauinstrucao)
  const formacao = useSelector<IStoreRedux, string | null>(state => state.dados.formacao)
  const idFormacao = useSelector<IStoreRedux, number[] | null>(state => state.dados.novaformacao)
  const dispatch = useDispatch()

  const [escolaridades, setEscolaridades] = useState<IEscolaridade[]>([])
  const [formacoes, setFormacoes] = useState<IFormacao[]>([])
  const [exibeFormacoes, setExibeFormacoes] = useState<boolean>(false)
  const [idEscolaridadesSemFormacao] = useState<number[]>([1, 2, 3, 4, 5, 6, 7])
  const [escValido, setEscValido] = useState(null as boolean | null);
  const [forValido, setForValido] = useState(null as boolean | null);

  const refPesquisaFormacao = useRef(-1)

  const obtemEscolaridades = useCallback(async () => {
    dispatch({ type: 'ALTERA_CARREGANDO', carregando: true })
    const retorno = await get('api-pessoas', `/escolaridades?nome=`, {})
    if (retorno && retorno?.status === 200) {
      setEscolaridades(retorno.data)
    }
    dispatch({ type: 'ALTERA_CARREGANDO', carregando: false })
  }, [dispatch])

  const pesquisaFormacoes = useCallback(async (palavraChave: string) => {
    dispatch({ type: 'ALTERA_CARREGANDO', carregando: true })
    const retorno = await get('api-pessoas', `/formacoes?nome=${palavraChave}&limit=100`, {})
    if (retorno && retorno?.status === 200) {
      setFormacoes(retorno.data)
    }
    dispatch({ type: 'ALTERA_CARREGANDO', carregando: false })
  }, [dispatch])

  const aoSelecionarEscolaridade = useCallback((_, value: IEscolaridade | null) => {
    if (!value) return

    const { id, nome } = value
    dispatch({ type: 'ALTERA_ESCOLARIDADE', grauinstrucao: id, escolaridade: nome })
    dispatch({ type: 'ALTERA_FORMACAO', novaformacao: null, formacao: null })
  }, [dispatch])

  const aoSelecionarFormacao = useCallback((_, value: IFormacao | null) => {
    if (!value) return

    const { id, nome } = value
    dispatch({ type: 'ALTERA_FORMACAO', novaformacao: [id], formacao: nome })
  }, [dispatch])

  const aoBuscarEscolaridade = useCallback((_, value: string) => {
    dispatch({ type: 'ALTERA_ESCOLARIDADE', grauinstrucao: null, escolaridade: value })
    dispatch({ type: 'ALTERA_FORMACAO', novaformacao: null, formacao: null })
  }, [dispatch])

  const aoBuscarFormacao = useCallback((_, value: string) => {
    window.clearTimeout(refPesquisaFormacao.current)
    dispatch({ type: 'ALTERA_FORMACAO', novaformacao: null, formacao: value })
    refPesquisaFormacao.current = window.setTimeout(() => pesquisaFormacoes(value), 800)
  }, [dispatch, pesquisaFormacoes])

  // Carrega escolaridades ao montar
  useEffect(() => { obtemEscolaridades() }, [dispatch, obtemEscolaridades])

  // Carrega formações quando necessário
  useEffect(() => {
    if (exibeFormacoes) {
      if (formacao === null || formacao === '') {
        pesquisaFormacoes('')
      } else {
        pesquisaFormacoes(formacao)
      }
    }
  }, [exibeFormacoes])

  // Controla visibilidade do campo de formação
  useEffect(() => {
    const toggleFormacao = !idEscolaridade ? false : !idEscolaridadesSemFormacao.includes(idEscolaridade)
    setExibeFormacoes(toggleFormacao)
  }, [idEscolaridade, idEscolaridadesSemFormacao])

  // Validação
  useEffect(() => {
    const regexId = /^\d{1,}$/
    const escolaridadeValida = idEscolaridade !== null ? regexId.test(String(idEscolaridade)) : null;

    // Se não exibe formação, valida apenas escolaridade
    if (!exibeFormacoes) {
      dispatch({ type: 'VALIDA_CAMPO', escolaridade: escolaridadeValida })
      setEscValido(escolaridadeValida)
    } else {
      // Se exibe formação, valida ambos
      const formacaoValida = idFormacao !== null ? (
        Array.isArray(idFormacao) && idFormacao.length > 0 && regexId.test(String(idFormacao[0]))
      ) : null;
      const ambosValidos = escolaridadeValida && formacaoValida
      dispatch({ type: 'VALIDA_CAMPO', escolaridade: ambosValidos })
      setEscValido(escolaridadeValida)
      setForValido(formacaoValida)
    }
  }, [dispatch, idEscolaridade, idFormacao, exibeFormacoes])

  return (
    <div className="campo-formulario">
      <Autocomplete
        freeSolo
        disableClearable
        autoHighlight
        fullWidth
        getOptionLabel={option => option.nome}
        options={escolaridades}
        onChange={aoSelecionarEscolaridade}
        onInputChange={aoBuscarEscolaridade}
        renderInput={(params) =>
          <CampoDeTextoOutlined
            {...params}
            InputProps={{ ref: params.InputProps.ref }}
            fullWidth
            error={escValido === false}
            label='Qual seu nível de escolaridade?'
            helperText={escValido === null && <Typography color="error">É obrigatório selecionar um valor da lista</Typography>}
            inputProps={{
              ...params.inputProps,
              autoComplete: 'new-password',
              value: escolaridade || ''
            }}
          />
        }
      />

      {exibeFormacoes && (
        <Autocomplete
          freeSolo
          clearOnEscape
          disableClearable
          autoHighlight
          fullWidth
          getOptionLabel={option => option.nome}
          options={formacoes}
          onChange={aoSelecionarFormacao}
          onInputChange={aoBuscarFormacao}
          renderInput={(params) =>
            <CampoDeTextoOutlined
              {...params}
              InputProps={{ ref: params.InputProps.ref }}
              fullWidth
              error={forValido === false}
              label='Qual sua formação?'
              helperText={forValido === null && <Typography color="error">É obrigatório selecionar um valor da lista</Typography>}
              style={{ marginTop: '16px' }}
              inputProps={{
                ...params.inputProps,
                autoComplete: 'new-password',
                value: formacao || ''
              }}
            />
          }
        />
      )}
    </div>
  )
}

export default CampoEscolaridade
